import React, { useEffect, useState } from "react";
import axios from "axios";
import { Person, EventDto } from "./types";
import "./../Event/AdminEvent.css";

interface Country {
    code: string;
}

type PersonFormData = Person & {
    uploadImageBase64?: string;
};

interface Props {
    initialPerson?: Person | null;
    onSave: (person: PersonFormData) => void;
    onCancel: () => void;
    allEvents: EventDto[];
    allFunctions: string[];
    currentUserId: number;
}

const PersonModalForm: React.FC<Props> = ({
    initialPerson,
    onSave,
    onCancel,
    allEvents,
    allFunctions,
    currentUserId,
}) => {
    const isEdit = !!initialPerson?.personId;

    const [countries, setCountries] = useState<Country[]>([]);
    const [busy, setBusy] = useState(false);

    const [formData, setFormData] = useState<PersonFormData>(() => ({
        personId: initialPerson?.personId ?? 0,
        userId: initialPerson?.userId ?? currentUserId,
        firstName: initialPerson?.firstName ?? "",
        lastName: initialPerson?.lastName ?? "",
        dateOfBirth: initialPerson?.dateOfBirth,
        country: initialPerson?.country ?? "",
        title: initialPerson?.title ?? "",
        company: initialPerson?.company ?? "",
        personImagePath: initialPerson?.personImagePath ?? "",
        profileUrl: initialPerson?.profileUrl ?? "",
        events: initialPerson?.events ?? [],
        functions: initialPerson?.functions ?? [],
        uploadImageBase64: undefined,
        created: initialPerson?.created ?? new Date().toISOString(),
        updated: initialPerson?.updated,
        zones: initialPerson?.zones ?? [],
        hasProfileImage: initialPerson?.hasProfileImage ?? false,
    }));

    const [previewImage, setPreviewImage] = useState<string | undefined>(
        initialPerson?.personImagePath || initialPerson?.profileUrl || undefined
    );

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/countries`);
                setCountries(res.data);
            } catch (err) {
                console.error("Failed to load countries", err);
            }
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        setFormData({
            personId: initialPerson?.personId ?? 0,
            userId: initialPerson?.userId ?? currentUserId,
            firstName: initialPerson?.firstName ?? "",
            lastName: initialPerson?.lastName ?? "",
            dateOfBirth: initialPerson?.dateOfBirth,
            country: initialPerson?.country ?? "",
            title: initialPerson?.title ?? "",
            company: initialPerson?.company ?? "",
            personImagePath: initialPerson?.personImagePath ?? "",
            profileUrl: initialPerson?.profileUrl ?? "",
            events: initialPerson?.events ?? [],
            functions: initialPerson?.functions ?? [],
            uploadImageBase64: undefined,
            created: initialPerson?.created ?? new Date().toISOString(),
            updated: initialPerson?.updated,
            zones: initialPerson?.zones ?? [],
            hasProfileImage: initialPerson?.hasProfileImage ?? false,
        });

        setPreviewImage(initialPerson?.personImagePath || initialPerson?.profileUrl || undefined);
    }, [initialPerson, currentUserId]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onCancel();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onCancel]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            const result = reader.result as string;
            setPreviewImage(result);
            setFormData((prev) => ({
                ...prev,
                uploadImageBase64: result,
            }));
        };

        reader.readAsDataURL(file);
    };

    const removeTag = (field: "events" | "functions", idx: number) => {
        setFormData((prev) => ({
            ...prev,
            [field]: (prev[field] as any[]).filter((_, i) => i !== idx),
        }));
    };

    const addEvent = (ev: EventDto) => {
        setFormData((prev) => {
            if ((prev.events ?? []).some((e) => e.eventID === ev.eventID)) return prev;
            return { ...prev, events: [...(prev.events ?? []), ev] };
        });
    };

    const addFunction = (value: string) => {
        setFormData((prev) => {
            const existing = prev.functions ?? [];
            if (existing.includes(value)) return prev;
            return { ...prev, functions: [...existing, value] };
        });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.firstName.trim() || !formData.lastName.trim()) {
            alert("Fornavn og efternavn er påkrævet");
            return;
        }

        const payload: PersonFormData = {
            ...formData,
            userId: isEdit ? formData.userId : currentUserId,
            created: isEdit ? formData.created : new Date().toISOString(),
            events: formData.events ?? [],
            functions: formData.functions ?? [],
        };

        try {
            setBusy(true);
            onSave(payload);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onCancel}>
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="eventform-card">
                    <div className="eventform-header">
                        <h2 className="eventform-title">{isEdit ? "Rediger Person" : "Tilføj Person"}</h2>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="eventform-grid">
                            <div className="field">
                                <label>Fornavn</label>
                                <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required />
                            </div>

                            <div className="field">
                                <label>Efternavn</label>
                                <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required />
                            </div>

                            <div className="field">
                                <label>Fødselsdato</label>
                                <input
                                    type="date"
                                    name="dateOfBirth"
                                    value={formData.dateOfBirth ? formData.dateOfBirth.split("T")[0] : ""}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="field">
                                <label>Land</label>
                                <select
                                    name="country"
                                    value={formData.country || ""}
                                    onChange={handleChange}
                                    className="styled-dropdown"
                                    required
                                >
                                    <option value="">Vælg land</option>
                                    {countries.map((c) => (
                                        <option key={c.code} value={c.code}>
                                            {c.code}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="field">
                                <label>Title</label>
                                <input type="text" name="title" value={formData.title || ""} onChange={handleChange} placeholder="e.g. Manager" />
                            </div>

                            <div className="field">
                                <label>Company</label>
                                <input type="text" name="company" value={formData.company || ""} onChange={handleChange} placeholder="e.g. Microsoft" />
                            </div>

                            <div className="field" style={{ gridColumn: "1 / -1" }}>
                                <label>Events</label>

                                <div className="chip-row">
                                    {(formData.events ?? []).map((ev, idx) => (
                                        <span className="chip" key={ev.eventID}>
                                            <span className="chip-text">{ev.eventName}</span>
                                            <button type="button" className="chip-x" onClick={() => removeTag("events", idx)}>
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>

                                <select
                                    className="styled-dropdown"
                                    defaultValue=""
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (!value) return;

                                        const ev = allEvents.find((x) => x.eventName === value);
                                        if (ev) addEvent(ev);

                                        e.target.value = "";
                                    }}
                                >
                                    <option value="">Tilføj event</option>
                                    {allEvents
                                        .filter((ev) => !(formData.events ?? []).some((s) => s.eventID === ev.eventID))
                                        .map((ev) => (
                                            <option key={ev.eventID} value={ev.eventName}>
                                                {ev.eventName}
                                            </option>
                                        ))}
                                </select>
                            </div>

                            <div className="field">
                                <label>Funktioner</label>

                                <div className="chip-row">
                                    {(formData.functions ?? []).map((fn, idx) => (
                                        <span className="chip" key={fn}>
                                            <span className="chip-text">{fn}</span>
                                            <button type="button" className="chip-x" onClick={() => removeTag("functions", idx)}>
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>

                                <select
                                    className="styled-dropdown"
                                    defaultValue=""
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (!value) return;

                                        addFunction(value);
                                        e.target.value = "";
                                    }}
                                >
                                    <option value="">Tilføj funktion</option>
                                    {allFunctions
                                        .filter((fn) => !(formData.functions ?? []).includes(fn))
                                        .map((fn) => (
                                            <option key={fn} value={fn}>
                                                {fn}
                                            </option>
                                        ))}
                                </select>
                            </div>

                            <div className="field">
                                <label>Profilbillede</label>
                                {previewImage && (
                                    <div style={{ marginBottom: 10 }}>
                                        <img
                                            src={previewImage}
                                            alt="Preview"
                                            style={{ width: 120, height: 120, objectFit: "cover", borderRadius: "50%" }}
                                        />
                                    </div>
                                )}
                                <input type="file" accept="image/*" capture="environment" onChange={handleImageChange} />
                            </div>
                        </div>

                        <div className="eventform-actions">
                            <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
                                Luk
                            </button>

                            <button type="submit" className="btn btn-primary" disabled={busy}>
                                {busy ? "Gemmer..." : "Gem"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PersonModalForm;